import React, { useEffect, useRef } from 'react'
import { KiteAlert, KiteIcon, KiteMenu, KiteMenuItem, KiteTooltip } from '@kite/react-kite'
import { copyToClipBoard } from '../../../utils/utils'
import CustomTable, { Spacing } from '../../../components/table/Table'
import type { AppDispatch, RootState } from '../../../redux/Store'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchData,
  setError,
  updateFilters,
  updatePagination,
  updateSort,
} from '../../../redux/Duplicates/DuplicatesFileSetupSlice'
import CustomToast from '../../../components/toast/CustomToast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PATHS } from '../../../routes/Routes'

interface ITableProps {
  spacing: Spacing
}

const DuplicateActivityTable = ({ spacing = Spacing.Default }: ITableProps) => {
  const isInitialMount = useRef(true)
  const dispatch: AppDispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const history = useNavigate()
  const [toastStatus, setToastStatus] = React.useState(false)
  const [toastMsg, setToastMsg] = React.useState('')
  const {
    duplicateFileSetupData = [],
    error,
    status,
    tableFilters,
    tablePagination,
    tablePagination: { PageNumber, RecordsPerPage },
    tableSort,
  } = useSelector((state: RootState) => state.duplicatesFileSetupData)

  const columns = [
    {
      label: 'File Type',
      sortKey: 'ScheduleTypeAbbrev',
      accessorKey: 'ScheduleTypeAbbrev',
      fixed: 'left',
      width: '60px',
    },
    {
      label: 'Source Title',
      sortKey: 'SourceTitle',
      accessorKey: 'SourceTitle',
      width: '150px',
    },
    {
      label: 'Source Path',
      sortKey: 'LookupSource',
      accessorKey: 'LookupSource',
      width: '430px',
      render: (row: any) => {
        const handleCopy = () => {
          copyToClipBoard(row?.LookupSource)
          setToastStatus(true)
          setToastMsg('Copied Source Path')
        }
        return (
          <>
            {row?.LookupSource ? (
              <KiteTooltip
                className='cell-file-path'
                type='truncation'
                id='kt1'
                text='Click to copy'
                direction='bottom'
              >
                <span onClick={handleCopy}>
                  <span>{row?.LookupSource}</span>
                  <span className='cell-icon'>
                    <KiteIcon icon='ki-copy' />
                  </span>
                </span>
              </KiteTooltip>
            ) : (
              ''
            )}
          </>
        )
      },
    },
    {
      label: 'Copy To Path',
      sortKey: 'TargetPath',
      accessorKey: 'TargetPath',
      width: '480px',
      render: (row: any) => {
        const handleCopy = () => {
          copyToClipBoard(row?.TargetPath)
          setToastStatus(true)
          setToastMsg('Copied Copy To Path')
        }
        return (
          <KiteTooltip
            className='cell-file-path'
            type='truncation'
            id='kt1'
            text='Click to copy'
            direction='bottom'
          >
            <span onClick={handleCopy}>
              <span>{row?.TargetPath}</span>
              <span className='cell-icon'>
                <KiteIcon icon='ki-copy' />
              </span>
            </span>
          </KiteTooltip>
        )
      },
    },
    {
      label: 'Created By',
      sortKey: 'CreatedByPID',
      accessorKey: 'CreatedByPID',
      width: '200px',
    },
    {
      label: 'File Count',
      sortKey: 'CrosspointCount',
      accessorKey: 'CrosspointCount',
      width: '100px',
    },
    {
      label: 'Enabled',
      sortKey: 'Enabled',
      accessorKey: 'Enabled',
      width: '60px',
      render: (row: any) => {
        return <div>{row?.Enabled ? 'True' : 'False'}</div>
      },
    },
    {
      label: 'Actions',
      sortKey: 'actions',
      fixed: 'right',
      accessorKey: 'actions',
      width: '20px',
      render: (row: any) => {
        const handleSelect = (key: string) => () => {
          if (key === 'Edit') {
            history(PATHS.DUPLICATE_EDIT_FILE_SETUP + '/' + row?.SetupID, { state: row })
          }
          if (key === 'Activity') {
            history(
              `${PATHS.DUPLICATE_ACTIVITY}?FileType=${
                row?.SourceType === 'MSM Schedule Source' ? 'Schedules' : 'Verifications'
              }&DuplicateFolderPath=${row?.TargetPath}`,
            )
          }
          if (key === 'Delete') {
            history(PATHS.DUPLICATE_DELETE_FILE_SETUP + '/' + row?.SetupID, { state: row })
          }
        }
        return (
          <KiteMenu variant='flow'>
            <KiteMenuItem onSelect={handleSelect('Edit')}>
              <span className='flex-center'>
                <KiteIcon className='copy-icon' icon='ki-edit' />
                Edit
              </span>
            </KiteMenuItem>
            <KiteMenuItem onSelect={handleSelect('Delete')}>
              <span className='flex-center'>
                <KiteIcon className='copy-icon' icon='ki-trash' />
                Delete
              </span>
            </KiteMenuItem>
            <KiteMenuItem onSelect={handleSelect('Activity')}>
              <span className='flex-center'>
                <KiteIcon className='copy-icon' icon='ki-activity' />
                Activity
              </span>
            </KiteMenuItem>
          </KiteMenu>
        )
      },
    },
  ]

  const handleSort = (column: string) => {
    const currentSortKey = tableSort?.SortBy
    const sortDirection = tableSort?.SortOrder
    let newSortDirection: any = 'asc'
    if (currentSortKey === column) {
      if (!sortDirection) {
        newSortDirection = 'asc'
      } else if (sortDirection === 'asc') {
        newSortDirection = 'desc'
      } else if (sortDirection === 'desc') {
        newSortDirection = 'asc'
      }
    } else {
      newSortDirection = 'asc'
    }
    dispatch(updateSort({ SortBy: column, SortOrder: newSortDirection }))
  }

  useEffect(() => {
    // Default values
    const currentFiltersData: { [x: string]: any } = {
      ...tableFilters,
    }
    const currentSortData: { [x: string]: any } = { ...tableSort }
    const currentPaginationData: { [x: string]: any } = { ...tablePagination }
    // Values From URl
    const paramEntries: any = searchParams.entries()
    for (const entry of paramEntries) {
      const [param, value] = entry
      if (value) {
        if (param === 'PageNumber' || param === 'RecordsPerPage') {
          currentPaginationData[param] = value
        } else if (param === 'SortBy' || param === 'SortOrder') {
          currentSortData[param] = value
        } else if (param === 'Enabled') {
          currentFiltersData[param] = Number(value)
        } else {
          currentFiltersData[param] = value
        }
      }
    }
    dispatch(
      updateFilters({
        ...currentFiltersData,
      }),
    )
    dispatch(updateSort({ ...currentSortData }))
    dispatch(updatePagination({ ...currentPaginationData }))
  }, [])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    const reqPayload: { [x: string]: any } = {
      ...tableFilters,
      Enabled:
        tableFilters?.Enabled || tableFilters?.Enabled === 0 ? Number(tableFilters?.Enabled) : null,
      ...tableSort,
      ...tablePagination,
    }
    // Setting applied values to URL
    const params = new URLSearchParams()
    Object.keys(reqPayload).forEach((filterKey) => {
      if (reqPayload[filterKey] || reqPayload[filterKey] === 0 || reqPayload[filterKey] === false) {
        params.append(filterKey, reqPayload[filterKey])
      }
    })
    history({ search: params.toString() }, { replace: true })
    dispatch(
      fetchData(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        reqPayload,
      ),
    )
  }, [
    JSON.stringify({
      ...tableFilters,
      ...tablePagination,
      ...tableSort,
    }),
    isInitialMount.current,
  ])

  const handleClearError = () => {
    dispatch(setError(''))
  }

  const handlePagination = (name: string) => (value: number) => {
    dispatch(
      updatePagination({
        ...tablePagination,
        [name]: value,
        ...(name === 'RecordsPerPage' && { PageNumber: 1 }),
      }),
    )
  }

  const handleToastExpire = () => {
    setToastStatus(false)
    setToastMsg('')
  }

  const totalItems = Number(duplicateFileSetupData[0]?.MaxCount || 0)
  const isLoading = status === 'loading'

  return (
    <>
      {error && (
        <div className='alert-element'>
          <KiteAlert
            onClose={handleClearError}
            autoFocus={false}
            level='page'
            type='error'
            description={error}
            dismissible
          />
        </div>
      )}
      <CustomTable
        columns={columns}
        currentSortKey={tableSort?.SortBy}
        sortDirection={tableSort?.SortOrder}
        onSort={handleSort}
        data={duplicateFileSetupData || []}
        spacing={spacing}
        loading={isLoading}
        currentPage={PageNumber}
        onPageChange={handlePagination('PageNumber')}
        itemsPerPage={RecordsPerPage}
        onItemsPerPageChange={handlePagination('RecordsPerPage')}
        totalItems={totalItems}
        paginationVariant='label'
      />
      <CustomToast status={toastStatus} message={toastMsg} onExpire={handleToastExpire} />
    </>
  )
}

export default DuplicateActivityTable
